import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Modal, Form, Input, Select, Tag,
  Popconfirm, message, Typography, Alert, Card
} from 'antd';
import { PlusOutlined, DeleteOutlined, KeyOutlined, UserOutlined } from '@ant-design/icons';
import { usersApi } from '../api';
import { useAuth } from '../context/AuthContext';

interface User {
  id: number;
  name: string;
  username: string;
  role: string;
  createdAt: string;
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [generatedCreds, setGeneratedCreds] = useState<{ username: string; password: string } | null>(null);
  const [pwdModal, setPwdModal] = useState<{ open: boolean; id: number; username: string }>({
    open: false, id: 0, username: '',
  });
  const [form] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await usersApi.list();
      setUsers(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (values: any) => {
    setSaving(true);
    try {
      const r = await usersApi.create(values);
      const data = r.data;
      if (data.generatedPassword) {
        setGeneratedCreds({ username: data.username, password: data.generatedPassword });
      }
      message.success('Пользователь создан');
      setCreateModal(false);
      form.resetFields();
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await usersApi.remove(id);
      message.success('Пользователь удалён');
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ошибка');
    }
  };

  const handleChangePassword = async (values: any) => {
    setSaving(true);
    try {
      await usersApi.changePassword(pwdModal.id, values.password);
      message.success('Пароль изменён');
      setPwdModal({ open: false, id: 0, username: '' });
      pwdForm.resetFields();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Пользователи</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>
          Добавить
        </Button>
      </div>

      {generatedCreds && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setGeneratedCreds(null)}
          style={{ marginBottom: 12 }}
          message="Сохраните данные для входа"
          description={
            <div>
              <div>Логин: <b>{generatedCreds.username}</b></div>
              <div>Пароль: <b>{generatedCreds.password}</b></div>
            </div>
          }
        />
      )}

      {loading
        ? <div style={{ textAlign: 'center', padding: 32, color: '#999' }}>Загрузка...</div>
        : users.map((u) => (
          <Card
            key={u.id}
            size="small"
            style={{ marginBottom: 8, borderRadius: 10 }}
            styles={{ body: { padding: '10px 12px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                  <UserOutlined style={{ color: '#764ba2' }} />
                  <b style={{ fontSize: 14 }}>{u.name || u.username}</b>
                  {u.id === me?.id && <Tag color="blue" style={{ margin: 0 }}>Вы</Tag>}
                  <Tag
                    color={u.role === 'admin' ? 'purple' : 'default'}
                    style={{ margin: 0, fontSize: 11 }}
                  >
                    {u.role === 'admin' ? 'Администратор' : 'Пользователь'}
                  </Tag>
                </div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  @{u.username} · {new Date(u.createdAt).toLocaleDateString('ru-RU')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <Button
                  size="small"
                  icon={<KeyOutlined />}
                  onClick={() => setPwdModal({ open: true, id: u.id, username: u.username })}
                />
                <Popconfirm
                  title={`Удалить "${u.name || u.username}"?`}
                  onConfirm={() => handleDelete(u.id)}
                  disabled={u.id === me?.id}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={u.id === me?.id}
                  />
                </Popconfirm>
              </div>
            </div>
          </Card>
        ))
      }

      {/* Модалка создания */}
      <Modal
        title="Новый пользователь"
        open={createModal}
        onCancel={() => { setCreateModal(false); form.resetFields(); }}
        footer={null}
        width="min(480px, 96vw)"
        style={{ top: 8 }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Имя" rules={[{ required: true, message: 'Введите имя' }]}>
            <Input placeholder="Айбек Карагулов" />
          </Form.Item>
          <Form.Item name="username" label="Логин (если пусто — сгенерируется)">
            <Input prefix={<UserOutlined />} placeholder="aibek" />
          </Form.Item>
          <Form.Item name="password" label="Пароль (если пусто — сгенерируется)"
            rules={[{ min: 6, message: 'Минимум 6 символов' }]}>
            <Input.Password placeholder="минимум 6 символов" />
          </Form.Item>
          <Form.Item name="role" label="Роль" initialValue="user">
            <Select>
              <Select.Option value="user">Пользователь</Select.Option>
              <Select.Option value="admin">Администратор</Select.Option>
            </Select>
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setCreateModal(false); form.resetFields(); }}>Отмена</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Создать</Button>
          </div>
        </Form>
      </Modal>

      {/* Модалка смены пароля */}
      <Modal
        title={`Сменить пароль — ${pwdModal.username}`}
        open={pwdModal.open}
        onCancel={() => { setPwdModal({ open: false, id: 0, username: '' }); pwdForm.resetFields(); }}
        footer={null}
        width="min(480px, 96vw)"
        style={{ top: 8 }}
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical" onFinish={handleChangePassword} style={{ marginTop: 16 }}>
          <Form.Item name="password" label="Новый пароль"
            rules={[{ required: true }, { min: 6, message: 'Минимум 6 символов' }]}>
            <Input.Password placeholder="минимум 6 символов" />
          </Form.Item>
          <Form.Item name="confirm" label="Повторите пароль"
            dependencies={['password']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject('Пароли не совпадают');
                },
              }),
            ]}>
            <Input.Password placeholder="повторите пароль" />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setPwdModal({ open: false, id: 0, username: '' }); pwdForm.resetFields(); }}>
              Отмена
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>Сохранить</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
